import debounce from 'lodash/debounce'
import uniq from 'lodash/uniq'
import without from 'lodash/without'
import socket from '@/lib/socket'
import { SOCKET_MESSAGE_TYPES, type ListenerFunc } from '@/lib/socket/types'

/**
 * Keeps track of the number of queries using a liveKey
 */
const keys: Map<string, number> = new Map()

/**
 * debounce updating listeners to save roundtrips
 */
const updateListeners = debounce(() => {
  const liveKeys: string[] = []
  for (const [key, listenerCount] of keys) {
    if (listenerCount > 0) {
      liveKeys.push(key)
    }
  }
  socket.emit(SOCKET_MESSAGE_TYPES.WS_GQL_CLIENT_SUBSCRIBE, { liveKeys })
}, 100)

function addLiveKey(liveKey: string) {
  const count = (keys.get(liveKey) || 0) + 1
  keys.set(liveKey, count)

  updateListeners()
}

function removeLiveKey(liveKey: string) {
  const count = keys.get(liveKey)! - 1
  keys.set(liveKey, count)

  updateListeners()
}

socket.on('reconnect', () => {
  updateListeners()
})

/**
 * Create a subscription to listen to liveKey changes.
 * Use the returned `setLiveKeys` function to update the liveKey list (this can be
 * done any time and the given list will completely override the previous liveKeys.
 * The returned `unsubscribe` does the same as setLiveKeys([])
 *
 * In the background there is only one websocket connection which subscribes to
 * the union of liveKeys from all the created live subscriptions.
 * @param listener
 */
export default function createSubscription(listener: ListenerFunc) {
  /**
   * The keys are the currently used liveKeys and the values are the
   * unsubscribe functions
   */
  const liveKeys: Map<string, ListenerFunc> = new Map()

  const setLiveKeys = (newLiveKeys: string[]) => {
    const currentKeys = Array.from(liveKeys.keys())
    const keysToAdd = without(uniq(newLiveKeys), ...currentKeys)
    const keysToRemove = without(currentKeys, ...newLiveKeys)
    keysToAdd.forEach((liveKey) => {
      liveKeys.set(
        liveKey,
        socket.on(
          `${SOCKET_MESSAGE_TYPES.WS_GQL_CLIENT_RECEIVE}/${liveKey}`,
          listener
        )
      )
      addLiveKey(liveKey)
    })
    keysToRemove.forEach((liveKey) => {
      liveKeys.get(liveKey)!()
      liveKeys.delete(liveKey)
      removeLiveKey(liveKey)
    })
  }

  const unsubscribe = () => {
    setLiveKeys([])
  }

  return {
    setLiveKeys,
    unsubscribe,
  }
}
